import { readViewFlag } from "./feedbackViewRestore";

/** The page's words for {@link restoreDecision}'s notes. */
export interface RestoreDecisionOptions {
  /** What Apply loads, as in "no plan was on their screen" ("data", "plan"). */
  data: string;
  /** What the page edits, as in "unsaved buy edits" ("grid", "buy", "draft"). */
  edits: string;
  /** Whether the viewer has unsaved edits on the page. */
  hasUnsavedEdits?: boolean;
}

/** Whether a restore loads data, and the banner notes that explain it. */
export interface RestoreDecision {
  /** True only when the reporter had applied an unchanged selection. */
  apply: boolean;
  /** What the restore does not set back, or why it does not load data. */
  notes: string[];
}

/**
 * Read the page's view block (`applied`, `selector_dirty`, `unsaved_edits`)
 * for whether the restore should auto-Apply, and the notes to show.
 */
export function restoreDecision(
  view: Record<string, unknown>,
  { data, edits, hasUnsavedEdits = false }: RestoreDecisionOptions
): RestoreDecision {
  const notes: string[] = [];
  if (readViewFlag(view.unsaved_edits)) {
    notes.push(`The reporter's unsaved ${edits} edits are not restored.`);
  }
  const applied = readViewFlag(view.applied) === true;
  const dirty = readViewFlag(view.selector_dirty) === true;
  if (!applied) {
    notes.push(
      `The reporter had not applied — their selection is restored, but no ${data} was on their screen.`
    );
  } else if (dirty) {
    notes.push(
      `The reporter had changed the selection without applying — it is restored but not applied, so press Apply to load it (it differs from the ${data} they saw).`
    );
  } else if (hasUnsavedEdits) {
    notes.push(
      "You have unsaved edits on this page, so the reporter's selection was not applied — save or discard them, then press Apply."
    );
  }
  return { apply: applied && !dirty && !hasUnsavedEdits, notes };
}
